import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  Component,
  ContentChild,
  Input,
  TemplateRef,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { CasePipe, CaseType } from '../../../pipes/case.pipe';
import { BackButtonComponent } from '../buttons/back-button.component';
import { ActionRowComponent } from '../panels/action-row.component';
import {
  HeadingAppearance,
  HeadingComponent,
  HeadingLevel,
} from './heading.component';
import { HelpIconComponent } from './help-icon.component';
import { IconComponent } from './icon.component';
import { TagComponent } from './tag.component';

@Component({
  selector: 'app-page-header',
  imports: [
    CommonModule,
    ActionRowComponent,
    BackButtonComponent,
    RouterLink,
    CasePipe,
    TagComponent,
    HelpIconComponent,
    IconComponent,
    HeadingComponent,
  ],
  template: `
    <div>
      @if (backLabel) {
        <div class="tw-pb-2">
          <app-action-row>
            <app-back-button left1 [routerLink]="backRouterLink"
              >Back to {{ backLabel }}</app-back-button
            >
          </app-action-row>
        </div>
      }

      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between sm:tw-items-center tw-gap-2 tw-pb-2"
        [class]="layoutSize === 'h1' ? 'tw-pt-3' : null"
      >
        <div class="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
          @if (icon) {
            <app-icon [name]="icon" [size]="iconSize" />
          }
          @if (layoutSize === 'h1') {
            <div>
              @if (labelHeader) {
                <div
                  class="tw-font-bold  tw-text-[var(--sed-black-60-color)] tw-leading-none"
                >
                  {{ labelHeader }}
                </div>
              }

              <app-heading
                [level]="resolvedLevel"
                [label]="label | case: labelCase"
                [appearance]="appearance"
                [id]="labelId"
              />
            </div>
          } @else {
            <app-heading
              [level]="resolvedLevel"
              [label]="label | case: labelCase"
              [appearance]="appearance"
              [id]="labelId"
            />
          }
          @if (helpText) {
            <app-help-icon
              [helpText]="helpText"
              [ariaLabel]="helpAriaLabel"
            />
          }
          @if (prototype) {
            <app-tag type="Warn">Prototype</app-tag>
          }

          <ng-container *ngTemplateOutlet="leftTemplate"></ng-container>
        </div>
        <div class="tw-min-w-0" [class.tw-pt-[14px]]="labelHeader">
          <ng-container *ngTemplateOutlet="rightTemplate"></ng-container>
        </div>
      </div>
    </div>
    <div [class]="paddingClasses">
      @if (layoutSize !== 'h1') {
        <hr />
      }
    </div>
  `,
  styles: [``],
})
export class PageHeaderComponent {
  @ContentChild('right')
  rightTemplate: TemplateRef<any>;

  @ContentChild('left')
  leftTemplate: TemplateRef<any>;

  @Input()
  label: string;

  /**
   * Id applied to the heading element so a region (e.g. a radiogroup)
   * can be named by the visible heading via aria-labelledby.
   */
  @Input()
  labelId: string;

  /**
   * Small grey header label that is above the label.
   */
  @Input()
  labelHeader: string;

  @Input()
  backRouterLink: string;

  @Input()
  backLabel: string;

  @Input()
  labelCase: CaseType = 'sentence';

  /**
   * Semantic heading size. Prefer `level` + `appearance` when the outline
   * level must differ from the visual style. Maps to `level` when `level` is unset.
   */
  @Input()
  headerSize: HeaderSize = 'h2';

  /** Semantic heading level (document outline). Defaults from `headerSize` when unset. */
  @Input()
  level?: HeadingLevel;

  /**
   * Visual typography. Use when semantic level differs from the look users already see
   * (e.g. page title as h1 with h2 appearance).
   */
  @Input()
  appearance: HeadingAppearance = 'inherit';

  @Input()
  icon: string;

  @Input()
  iconSize: string;

  /**
   * If true, shows a prototype tag next to label. Be sure to
   * set to false before shipping to production.
   */
  @Input({ transform: booleanAttribute })
  prototype = false;

  @Input()
  helpText: string;

  /**
   * Concise accessible name for the help icon trigger.
   * Defaults to "{label} information" when a label is present.
   */
  @Input()
  helpIconAriaLabel: string;

  @Input()
  paddingClasses = 'tw-pb-6';

  protected get resolvedLevel(): HeadingLevel {
    if (this.level != null) {
      return this.level;
    }
    return Number(this.headerSize.slice(1)) as HeadingLevel;
  }

  /** Layout chrome (padding, hr) follows visual appearance, not semantic level. */
  protected get layoutSize(): HeaderSize {
    if (this.appearance !== 'inherit') {
      return this.appearance;
    }
    const level = this.resolvedLevel;
    if (level <= 1) {
      return 'h1';
    }
    if (level === 2) {
      return 'h2';
    }
    if (level === 3) {
      return 'h3';
    }
    return 'h4';
  }

  protected get helpAriaLabel(): string {
    if (this.helpIconAriaLabel) {
      return this.helpIconAriaLabel;
    }
    return this.label ? `${this.label} information` : 'More information';
  }
}

export type HeaderSize = 'h1' | 'h2' | 'h3' | 'h4';
